import * as React from 'react';

import {
  CalendarDate,
  CalendarDateTime,
  getLocalTimeZone,
  Time,
  today,
  toZoned,
} from '@internationalized/date';
import { Unit } from '@rematter/frontend-lib-api-client';
import { useToast } from '@rematter/ui-web/hooks';
import { getDefaultStore } from 'jotai';

import { $evaluateAdHocFormulas } from '../../../_graphql/queryEvaluateAdHocFormulas';
import type { EvaluatedTierData } from '../../../_graphql/queryEvaluateFormulas';
import { useTranslations } from '../../../i18n/useTranslations';
import type {
  ItemPricingRecord,
  TierInput,
  TierRow,
  VariableRecord,
} from '../../../types/pricing';

import {
  buildTierInputFromEdit,
  flattenToTierRows,
  type SelectedCell,
  type TierEditValues,
  tierEditValuesFromTierRow,
  tierRecordToInput,
} from './useProductPricingColumns';

const PRICE_FIELDS = new Set(['targetPrice', 'minPrice', 'maxPrice']);

export type BatchSaveItem = {
  itemPricingId: string;
  input: {
    tiersEffectiveAt: string;
    itemId?: string;
    pricingUnit?: string;
  };
  tiers: TierInput[];
};

export type AddItemPricingInput = {
  itemId: string;
  pricingUnit: Unit;
  position: number;
  tiers: TierInput[];
};

export type AddProductPricingInput = AddItemPricingInput;

export type FlatTierRow = Omit<TierRow, 'tier'> & {
  tier: TierRow['tier'] | null;
  isFirstTierOfProduct: boolean;
  isNewRow: boolean;
  minWeight: number | null;
  isPendingDelete?: boolean;
};

export type NewInlineRowData = {
  id: string;
  itemId: string;
  itemName: string;
  itemGroupName: string | null;
  pricingUnit: string;
  parentItemPricingId: string | null;
  tierEditValues: TierEditValues;
};

const emptyTierEditValues = (): TierEditValues => ({
  maxAmount: '',
  targetPrice: '',
  minPrice: '',
  maxPrice: '',
  scrapValue: '0',
  minPriceVariableIds: [],
  targetPriceVariableIds: [],
  maxPriceVariableIds: [],
});

let inlineRowCounter = 0;
const nextInlineRowId = () => `inline-${++inlineRowCounter}-${Date.now()}`;

type UseEditablePricingTableParams = {
  pricingsList: ItemPricingRecord[];
  evaluatedPrices: Record<string, EvaluatedTierData>;
  variables: VariableRecord[];
  selectedCell: SelectedCell | null;
  setSelectedCell: (cell: SelectedCell | null) => void;
  pricingMode?: 'Standard' | 'CRV';
  listId: string;

  batchSave: (params: {
    listId: string;
    items: BatchSaveItem[];
  }) => Promise<unknown>;
  deleteItemPricing: (params: { itemPricingId: string }) => Promise<unknown>;
  addItemPricing: (input: AddItemPricingInput) => Promise<unknown>;
  reorderItemPricings?: (params: {
    itemPricingIds: string[];
  }) => Promise<unknown>;

  refetchItemPricings: () => void;
  refetchEvaluatedFormulas: () => void;
};

export const useEditablePricingTable = ({
  pricingsList,
  evaluatedPrices,
  variables,
  selectedCell,
  setSelectedCell,
  pricingMode,
  listId,
  batchSave,
  deleteItemPricing,
  addItemPricing,
  reorderItemPricings,
  refetchItemPricings,
  refetchEvaluatedFormulas,
}: UseEditablePricingTableParams) => {
  const t = useTranslations();
  const toast = useToast();

  const [isAddDialogOpen, setIsAddDialogOpen] = React.useState(false);
  const [isVariablesPanelOpen, setIsVariablesPanelOpen] = React.useState(false);
  const [addTierTarget, setAddTierTarget] = React.useState<{
    itemPricingId: string;
    lastMaxAmount: number | null;
  } | null>(null);

  const [pendingChanges, setPendingChanges] = React.useState<
    Map<string, TierEditValues>
  >(new Map());
  const [effectiveDate, setEffectiveDate] = React.useState<CalendarDate>(
    today(getLocalTimeZone()),
  );
  const [effectiveTime, setEffectiveTime] = React.useState(() => {
    const now = new Date();
    return new Time(now.getHours(), now.getMinutes());
  });

  const [newInlineRows, setNewInlineRows] = React.useState<NewInlineRowData[]>(
    [],
  );
  const [reorderPending, setReorderPending] = React.useState<string[] | null>(
    null,
  );
  const [pendingDeletes, setPendingDeletes] = React.useState<Set<string>>(
    new Set(),
  );
  const [pendingTierDeletes, setPendingTierDeletes] = React.useState<
    Set<string>
  >(new Set());
  const [validationErrors, setValidationErrors] = React.useState<Set<string>>(
    new Set(),
  );
  const [pendingFocusRowId, setPendingFocusRowId] = React.useState<
    string | null
  >(null);

  type AdHocEntry = { formula: string; value: number };
  type AdHocMap = Map<string, Record<string, AdHocEntry>>;
  const [adHocEvaluated, setAdHocEvaluated] = React.useState<AdHocMap>(
    new Map(),
  );
  const evalTimerRef = React.useRef<ReturnType<typeof setTimeout>>(undefined);

  const evaluateFormula = React.useCallback(
    (tierId: string, field: string, formula: string) => {
      if (
        !formula.trim() ||
        (!formula.includes('$') && !/[+\-*/]/.test(formula))
      ) {
        return;
      }

      clearTimeout(evalTimerRef.current);
      evalTimerRef.current = setTimeout(async () => {
        try {
          const store = getDefaultStore();
          const results = await store.set($evaluateAdHocFormulas, [formula]);
          const result = results[0];
          if (result && result.value != null && !result.error) {
            const evaluatedValue = result.value;
            setAdHocEvaluated((prev) => {
              const next = new Map(prev);
              const existing = next.get(tierId) ?? {};
              next.set(tierId, {
                ...existing,
                [field]: { formula, value: evaluatedValue },
              });
              return next;
            });
          }
        } catch (e) {
          console.error(e);
        }
      }, 500);
    },
    [],
  );

  const tierRows = React.useMemo(
    () => flattenToTierRows(pricingsList),
    [pricingsList],
  );

  const flatTierRows = React.useMemo(() => {
    const existingRows: FlatTierRow[] = [];
    const orderedPricings = reorderPending
      ? (reorderPending
          .map((id) => pricingsList.find((pp) => pp.id === id))
          .filter(Boolean) as ItemPricingRecord[])
      : pricingsList;

    const orderedTierRows = flattenToTierRows(orderedPricings);

    const activeTierRows = orderedTierRows.filter(
      (r) => !pendingTierDeletes.has(r.id),
    );

    for (let ai = 0; ai < activeTierRows.length; ai++) {
      const row = activeTierRows[ai];
      const isFirstOfItem =
        ai === 0 || activeTierRows[ai - 1].itemPricingId !== row.itemPricingId;
      let minWeight: number | null = null;
      if (isFirstOfItem) {
        minWeight = 0;
      } else {
        const prevRow = activeTierRows[ai - 1];
        if (prevRow) {
          const prevPending = pendingChanges.get(prevRow.id);
          const prevMax = prevPending
            ? Number(prevPending.maxAmount)
            : prevRow.tier?.maxAmount;
          minWeight =
            prevMax != null && !isNaN(Number(prevMax)) ? Number(prevMax) : null;
        }
      }

      existingRows.push({
        ...row,
        isFirstTierOfProduct: isFirstOfItem,
        isNewRow: false,
        minWeight,
        isPendingDelete: pendingDeletes.has(row.itemPricingId),
      });

      const isLastActiveOfItem =
        ai === activeTierRows.length - 1 ||
        activeTierRows[ai + 1].itemPricingId !== row.itemPricingId;
      if (isLastActiveOfItem) {
        const inlineTiers = newInlineRows.filter(
          (nr) => nr.parentItemPricingId === row.itemPricingId,
        );
        for (let i = 0; i < inlineTiers.length; i++) {
          const nr = inlineTiers[i];
          let inlineMinWeight: number | null = null;
          if (i === 0) {
            const prevPending = pendingChanges.get(row.id);
            const prevMax = prevPending
              ? Number(prevPending.maxAmount)
              : row.tier?.maxAmount;
            inlineMinWeight =
              prevMax != null && !isNaN(Number(prevMax))
                ? Number(prevMax)
                : null;
          } else {
            const prevInline = inlineTiers[i - 1];
            const prevMax = prevInline.tierEditValues.maxAmount;
            inlineMinWeight =
              prevMax && !isNaN(Number(prevMax)) ? Number(prevMax) : null;
          }
          existingRows.push({
            id: nr.id,
            itemPricingId: row.itemPricingId,
            itemId: row.itemId,
            itemName: row.itemName,
            itemGroupName: row.itemGroupName,
            pricingUnit: row.pricingUnit,
            itemPricingPosition: row.itemPricingPosition,
            tier: null,
            tierCount: row.tierCount + inlineTiers.length,
            tierIndex: row.tierCount + i,
            isFirstTierOfProduct: false,
            isNewRow: true,
            minWeight: inlineMinWeight,
          });
        }
      }
    }

    const newProductRows = newInlineRows.filter(
      (nr) => !nr.parentItemPricingId,
    );
    for (let i = 0; i < newProductRows.length; i++) {
      const nr = newProductRows[i];
      const newRow: FlatTierRow = {
        id: nr.id,
        itemPricingId: nr.id,
        itemId: nr.itemId,
        itemName: nr.itemName,
        itemGroupName: nr.itemGroupName,
        pricingUnit: nr.pricingUnit as Unit,
        itemPricingPosition: orderedPricings.length + i,
        tier: null,
        tierCount: 1,
        tierIndex: 0,
        isFirstTierOfProduct: true,
        isNewRow: true,
        minWeight: 0,
      };

      if (nr.itemGroupName) {
        let lastGroupIdx = -1;
        for (let j = existingRows.length - 1; j >= 0; j--) {
          if (
            existingRows[j].itemGroupName === nr.itemGroupName &&
            existingRows[j].id !== 'empty-bottom'
          ) {
            lastGroupIdx = j;
            break;
          }
        }
        if (lastGroupIdx !== -1) {
          existingRows.splice(lastGroupIdx + 1, 0, newRow);
          continue;
        }
      }
      existingRows.push(newRow);
    }

    if (!reorderPending) {
      const groupFirstIndex = new Map<string | null, number>();
      for (let i = 0; i < existingRows.length; i++) {
        const g = existingRows[i].itemGroupName;
        if (!groupFirstIndex.has(g)) {
          groupFirstIndex.set(g, i);
        }
      }
      existingRows.sort((a, b) => {
        const ga = groupFirstIndex.get(a.itemGroupName) ?? existingRows.length;
        const gb = groupFirstIndex.get(b.itemGroupName) ?? existingRows.length;
        return ga - gb;
      });
    }

    existingRows.push({
      id: 'empty-bottom',
      itemPricingId: 'empty-bottom',
      itemId: '',
      itemName: '',
      itemGroupName: null,
      pricingUnit: '' as Unit,
      itemPricingPosition: existingRows.length,
      tier: null,
      tierCount: 1,
      tierIndex: 0,
      isFirstTierOfProduct: true,
      isNewRow: true,
      minWeight: null,
    });

    return existingRows;
  }, [
    pricingsList,
    newInlineRows,
    reorderPending,
    pendingChanges,
    pendingDeletes,
    pendingTierDeletes,
  ]);

  const addEmptyRow = React.useCallback(() => false, []);

  const removeLastEmptyRow = React.useCallback(() => {
    const newProductRows = newInlineRows.filter(
      (nr) => !nr.parentItemPricingId,
    );
    if (newProductRows.length === 0) return null;

    const last = newProductRows[newProductRows.length - 1];
    if (last.itemId || last.pricingUnit) return null;

    const removedId = last.id;
    setNewInlineRows((prev) => prev.filter((nr) => nr.id !== removedId));
    return flatTierRows.length - 2;
  }, [newInlineRows, flatTierRows.length]);

  const addTierRow = React.useCallback(
    (itemPricingId: string) => {
      const pp = pricingsList.find((p) => p.id === itemPricingId);
      if (!pp) return;

      const id = nextInlineRowId();
      setNewInlineRows((prev) => [
        ...prev,
        {
          id,
          itemId: pp.item.id,
          itemName: pp.item.name,
          itemGroupName: pp.item.itemGroup?.name ?? null,
          pricingUnit: pp.pricingUnit,
          parentItemPricingId: itemPricingId,
          tierEditValues: emptyTierEditValues(),
        },
      ]);
    },
    [pricingsList],
  );

  const deleteTier = React.useCallback(
    (tierId: string) => {
      const inlineRow = newInlineRows.find((nr) => nr.id === tierId);
      if (inlineRow) {
        setNewInlineRows((prev) => prev.filter((nr) => nr.id !== tierId));
      } else {
        setPendingTierDeletes((prev) => {
          const next = new Set(prev);
          next.add(tierId);
          return next;
        });
      }
    },
    [newInlineRows],
  );

  const reorderItems = React.useCallback(
    (activeId: string, overId: string) => {
      const currentIds = reorderPending ?? pricingsList.map((pp) => pp.id);
      const activeIdx = currentIds.indexOf(activeId);
      const overIdx = currentIds.indexOf(overId);
      if (activeIdx === -1 || overIdx === -1 || activeIdx === overIdx) return;

      const newIds = [...currentIds];
      const [moved] = newIds.splice(activeIdx, 1);
      newIds.splice(overIdx, 0, moved);
      setReorderPending(newIds);
    },
    [pricingsList, reorderPending],
  );

  const reorderGroup = React.useCallback(
    (groupName: string, targetGroupName: string) => {
      const currentIds = reorderPending ?? pricingsList.map((pp) => pp.id);
      const groupItemIds = flatTierRows
        .filter(
          (r) =>
            (r.itemGroupName ?? t.pricing.groups.ungrouped) === groupName &&
            r.isFirstTierOfProduct &&
            !r.isNewRow,
        )
        .map((r) => r.itemPricingId);
      const targetItemIds = flatTierRows
        .filter(
          (r) =>
            (r.itemGroupName ?? t.pricing.groups.ungrouped) ===
              targetGroupName &&
            r.isFirstTierOfProduct &&
            !r.isNewRow,
        )
        .map((r) => r.itemPricingId);

      if (groupItemIds.length === 0 || targetItemIds.length === 0) return;

      const withoutGroup = currentIds.filter(
        (id) => !groupItemIds.includes(id),
      );
      const targetFirstIdx = withoutGroup.indexOf(targetItemIds[0]);
      if (targetFirstIdx === -1) return;

      const sourceFirstIdx = currentIds.indexOf(groupItemIds[0]);
      const targetOrigFirstIdx = currentIds.indexOf(targetItemIds[0]);
      const insertBefore = sourceFirstIdx > targetOrigFirstIdx;

      const newIds = [...withoutGroup];
      newIds.splice(
        insertBefore ? targetFirstIdx : targetFirstIdx + targetItemIds.length,
        0,
        ...groupItemIds,
      );
      setReorderPending(newIds);
    },
    [pricingsList, reorderPending, flatTierRows, t],
  );

  const resolveVariableIds = React.useCallback(
    (formula: string): string[] => {
      const ids: string[] = [];
      for (const match of formula.matchAll(/\$([a-zA-Z_][a-zA-Z0-9_]*)/g)) {
        const variable = variables.find((v) => v.name === match[1]);
        if (variable) ids.push(variable.id);
      }
      return ids;
    },
    [variables],
  );

  const handleInlineRowChange = React.useCallback(
    (
      rowId: string,
      field: string,
      value:
        | string
        | {
            id: string;
            name: string;
            pricingUnit?: string;
            itemGroupName?: string;
          },
    ) => {
      let focusRowId: string | null = null;
      setNewInlineRows((prev) => {
        const isEmptyBottom = rowId === 'empty-bottom';
        const exists = prev.some((nr) => nr.id === rowId);

        if (isEmptyBottom && !exists) {
          const newId = nextInlineRowId();
          const newRow: NewInlineRowData = {
            id: newId,
            itemId: '',
            itemName: '',
            itemGroupName: null,
            pricingUnit: '',
            parentItemPricingId: null,
            tierEditValues: emptyTierEditValues(),
          };
          if (field === 'product' && typeof value === 'object') {
            newRow.itemId = value.id;
            newRow.itemName = value.name;
            newRow.itemGroupName = value.itemGroupName ?? null;
            if (value.pricingUnit) {
              newRow.pricingUnit = value.pricingUnit;
            }
            focusRowId = newId;
          } else if (field === 'pricingUnit' && typeof value === 'string') {
            newRow.pricingUnit = value;
          } else if (typeof value === 'string') {
            newRow.tierEditValues = {
              ...newRow.tierEditValues,
              [field]: value,
              ...(PRICE_FIELDS.has(field)
                ? {
                    [`${field}VariableIds`]: resolveVariableIds(value),
                  }
                : {}),
            };
          }
          return [...prev, newRow];
        }

        let updated = prev.map((nr) => {
          if (nr.id !== rowId) return nr;
          if (field === 'product' && typeof value === 'object') {
            focusRowId = nr.id;
            return {
              ...nr,
              itemId: value.id,
              itemName: value.name,
              itemGroupName: value.itemGroupName ?? null,
              ...(value.pricingUnit ? { pricingUnit: value.pricingUnit } : {}),
            };
          }
          if (field === 'pricingUnit' && typeof value === 'string') {
            return { ...nr, pricingUnit: value };
          }
          return {
            ...nr,
            tierEditValues: {
              ...nr.tierEditValues,
              [field]: value,
              ...(PRICE_FIELDS.has(field) && typeof value === 'string'
                ? {
                    [`${field}VariableIds`]: resolveVariableIds(value),
                  }
                : {}),
            },
          };
        });

        if (
          field === 'maxAmount' &&
          typeof value === 'string' &&
          value.trim() !== ''
        ) {
          const nr = updated.find((r) => r.id === rowId);
          if (nr?.parentItemPricingId) {
            const siblings = updated.filter(
              (r) => r.parentItemPricingId === nr.parentItemPricingId,
            );
            if (siblings[siblings.length - 1]?.id === rowId) {
              const pp = pricingsList.find(
                (p) => p.id === nr.parentItemPricingId,
              );
              updated = [
                ...updated,
                {
                  id: nextInlineRowId(),
                  itemId: pp?.item.id ?? nr.itemId,
                  itemName: pp?.item.name ?? nr.itemName,
                  itemGroupName: pp?.item.itemGroup?.name ?? nr.itemGroupName,
                  pricingUnit: pp?.pricingUnit ?? nr.pricingUnit,
                  parentItemPricingId: nr.parentItemPricingId,
                  tierEditValues: emptyTierEditValues(),
                },
              ];
            }
          }
        }

        return updated;
      });
      if (focusRowId) {
        setPendingFocusRowId(focusRowId);
      }
    },
    [pricingsList, setPendingFocusRowId, resolveVariableIds],
  );

  const isInlineRow = React.useCallback(
    (tierId: string) =>
      tierId === 'empty-bottom' || newInlineRows.some((nr) => nr.id === tierId),
    [newInlineRows],
  );

  const getInlineRowData = React.useCallback(
    (rowId: string) => newInlineRows.find((nr) => nr.id === rowId) ?? null,
    [newInlineRows],
  );

  const selectedRow = React.useMemo(
    () =>
      selectedCell ? tierRows.find((r) => r.id === selectedCell.tierId) : null,
    [selectedCell, tierRows],
  );

  const selectedCellValue = React.useMemo(() => {
    if (!selectedCell) return null;
    const pending = pendingChanges.get(selectedCell.tierId);
    if (pending) return pending[selectedCell.field] as string;
    if (isInlineRow(selectedCell.tierId)) {
      const nr = getInlineRowData(selectedCell.tierId);
      if (nr) return nr.tierEditValues[selectedCell.field] as string;
      return '';
    }
    const row = tierRows.find((r) => r.id === selectedCell.tierId);
    if (!row) return null;
    return row.tier[selectedCell.field]?.formula ?? '';
  }, [selectedCell, pendingChanges, tierRows, isInlineRow, getInlineRowData]);

  const selectedCellEvaluatedValue = React.useMemo(() => {
    if (!selectedCell) return null;
    if (isInlineRow(selectedCell.tierId)) return null;
    const pending = pendingChanges.get(selectedCell.tierId);
    if (pending) {
      const row = tierRows.find((r) => r.id === selectedCell.tierId);
      const originalFormula = row?.tier?.[selectedCell.field]?.formula ?? '';
      const currentValue = pending[selectedCell.field] as string;
      if (currentValue !== originalFormula) {
        const adHocEntry = adHocEvaluated.get(selectedCell.tierId)?.[
          selectedCell.field
        ];
        if (adHocEntry && adHocEntry.formula === currentValue)
          return adHocEntry.value;
        return null;
      }
    }
    return evaluatedPrices[selectedCell.tierId]?.[selectedCell.field] ?? null;
  }, [
    selectedCell,
    pendingChanges,
    tierRows,
    evaluatedPrices,
    isInlineRow,
    adHocEvaluated,
  ]);

  const handleCellChange = React.useCallback(
    (tierId: string, itemPricingId: string, field: string, value: string) => {
      if (isInlineRow(tierId)) {
        handleInlineRowChange(tierId, field, value);
        if (PRICE_FIELDS.has(field)) {
          evaluateFormula(tierId, field, value);
        }
        return;
      }
      setPendingChanges((prev) => {
        const next = new Map(prev);
        let entry = next.get(tierId);
        if (!entry) {
          const row = tierRows.find((r) => r.id === tierId);
          if (!row) return prev;
          entry = tierEditValuesFromTierRow(row);
        }
        entry = { ...entry, [field]: value };
        if (PRICE_FIELDS.has(field)) {
          const idsKey = `${field}VariableIds` as keyof TierEditValues;
          entry = { ...entry, [idsKey]: resolveVariableIds(value) };
        }
        next.set(tierId, entry);
        return next;
      });
      const now = new Date();
      setEffectiveDate(today(getLocalTimeZone()));
      setEffectiveTime(new Time(now.getHours(), now.getMinutes()));
      if (PRICE_FIELDS.has(field)) {
        evaluateFormula(tierId, field, value);
      }

      if (field === 'maxAmount' && value.trim() !== '') {
        const row = tierRows.find((r) => r.id === tierId);
        if (row) {
          const pp = pricingsList.find((p) => p.id === row.itemPricingId);
          const isLastExistingTier =
            pp && row.tier && row.tier.position === pp.tiers.length - 1;
          const hasInlineTiers = newInlineRows.some(
            (nr) => nr.parentItemPricingId === row.itemPricingId,
          );
          if (isLastExistingTier && !hasInlineTiers) {
            addTierRow(row.itemPricingId);
          }
        }
      }
    },
    [
      tierRows,
      isInlineRow,
      handleInlineRowChange,
      newInlineRows,
      pricingsList,
      addTierRow,
      evaluateFormula,
      resolveVariableIds,
    ],
  );

  const handleFormulaBarChange = React.useCallback(
    (value: string) => {
      if (!selectedCell) return;
      if (isInlineRow(selectedCell.tierId)) {
        handleInlineRowChange(selectedCell.tierId, selectedCell.field, value);
        if (PRICE_FIELDS.has(selectedCell.field)) {
          evaluateFormula(selectedCell.tierId, selectedCell.field, value);
        }
        return;
      }
      if (selectedRow) {
        handleCellChange(
          selectedCell.tierId,
          selectedRow.itemPricingId,
          selectedCell.field,
          value,
        );
      }
    },
    [
      selectedCell,
      selectedRow,
      handleCellChange,
      isInlineRow,
      handleInlineRowChange,
      evaluateFormula,
    ],
  );

  const handleAddItemPricing = React.useCallback(
    async (input: AddItemPricingInput) => {
      await addItemPricing(input);
      toast.success(t.pricing.itemPricings.toast.added);
      refetchItemPricings?.();
      refetchEvaluatedFormulas?.();
    },
    [addItemPricing, toast, t, refetchItemPricings, refetchEvaluatedFormulas],
  );

  const handleDeleteItemPricing = React.useCallback(
    (itemPricingId: string) => {
      const isNewProductRow = newInlineRows.some(
        (nr) => nr.id === itemPricingId && !nr.parentItemPricingId,
      );
      if (isNewProductRow) {
        setNewInlineRows((prev) =>
          prev.filter(
            (nr) =>
              nr.id !== itemPricingId &&
              nr.parentItemPricingId !== itemPricingId,
          ),
        );
        return;
      }
      setPendingDeletes((prev) => {
        const next = new Set(prev);
        next.add(itemPricingId);
        return next;
      });
      setPendingChanges((prev) => {
        const pp = pricingsList.find((p) => p.id === itemPricingId);
        if (!pp) return prev;
        const next = new Map(prev);
        for (const tier of pp.tiers) {
          next.delete(tier.id);
        }
        return next;
      });
      setNewInlineRows((prev) =>
        prev.filter((nr) => nr.parentItemPricingId !== itemPricingId),
      );
    },
    [pricingsList, newInlineRows],
  );

  const handleCopyItemPricing = React.useCallback(
    (itemPricingId: string) => {
      const pp = pricingsList.find((p) => p.id === itemPricingId);
      if (!pp) return;
      const id = nextInlineRowId();
      const firstTier = pp.tiers[0];
      setNewInlineRows((prev) => [
        ...prev,
        {
          id,
          itemId: pp.item.id,
          itemName: pp.item.name,
          itemGroupName: pp.item.itemGroup?.name ?? null,
          pricingUnit: pp.pricingUnit,
          parentItemPricingId: null,
          tierEditValues: firstTier
            ? {
                maxAmount:
                  firstTier.maxAmount != null
                    ? String(firstTier.maxAmount)
                    : '',
                targetPrice: firstTier.targetPrice.formula ?? '',
                minPrice: firstTier.minPrice.formula ?? '',
                maxPrice: firstTier.maxPrice.formula ?? '',
                scrapValue: String(firstTier.scrapValue),
                minPriceVariableIds: firstTier.minPrice.variableIds ?? [],
                targetPriceVariableIds: firstTier.targetPrice.variableIds ?? [],
                maxPriceVariableIds: firstTier.maxPrice.variableIds ?? [],
              }
            : emptyTierEditValues(),
        },
      ]);
    },
    [pricingsList],
  );

  const handleAddTier = React.useCallback(
    (itemPricingId: string) => {
      const pp = pricingsList.find((p) => p.id === itemPricingId);
      if (!pp) return;
      const lastTier = pp.tiers[pp.tiers.length - 1];
      setAddTierTarget({
        itemPricingId,
        lastMaxAmount: lastTier?.maxAmount ?? null,
      });
    },
    [pricingsList],
  );

  const handleAddTierSubmit = React.useCallback(
    async (newTier: TierInput) => {
      if (!addTierTarget) return;
      const pp = pricingsList.find((p) => p.id === addTierTarget.itemPricingId);
      if (!pp) return;
      const tiers: TierInput[] = pp.tiers.map((tier, idx) => {
        const pending = pendingChanges.get(tier.id);
        if (pending) {
          return {
            ...buildTierInputFromEdit(pending, pricingMode),
            position: idx,
          };
        }
        return tierRecordToInput(tier, idx);
      });
      tiers.push({ ...newTier, position: tiers.length });
      await batchSave({
        listId,
        items: [
          {
            itemPricingId: addTierTarget.itemPricingId,
            input: {
              tiersEffectiveAt: new Date().toISOString(),
            },
            tiers,
          },
        ],
      });
      setPendingChanges((prev) => {
        const next = new Map(prev);
        for (const tier of pp.tiers) {
          next.delete(tier.id);
        }
        return next;
      });
      toast.success(t.pricing.itemPricings.toast.tierAdded);
      setAddTierTarget(null);
      refetchItemPricings?.();
      refetchEvaluatedFormulas?.();
    },
    [
      addTierTarget,
      pricingsList,
      pendingChanges,
      pricingMode,
      batchSave,
      listId,
      toast,
      t,
      refetchItemPricings,
      refetchEvaluatedFormulas,
    ],
  );

  const handleBatchSave = React.useCallback(async () => {
    const hasExistingChanges = pendingChanges.size > 0;
    const newProductRows = newInlineRows.filter(
      (nr) => !nr.parentItemPricingId && nr.itemId,
    );
    const newTierRows = newInlineRows.filter((nr) => nr.parentItemPricingId);
    const hasNewRows = newProductRows.length > 0 || newTierRows.length > 0;
    const hasReorder = reorderPending !== null;
    const hasDeletes = pendingDeletes.size > 0;

    if (
      !listId ||
      (!hasExistingChanges && !hasNewRows && !hasReorder && !hasDeletes)
    )
      return;

    try {
      const errorRowIds = new Set<string>();
      for (const [tierId, entry] of pendingChanges) {
        if (!entry.targetPrice) {
          errorRowIds.add(tierId);
        }
        const min = entry.minPrice ? Number(entry.minPrice) : NaN;
        const target = entry.targetPrice ? Number(entry.targetPrice) : NaN;
        const max = entry.maxPrice ? Number(entry.maxPrice) : NaN;
        if (!isNaN(min) && !isNaN(target) && min > target) {
          toast.error(t.pricing.saveToolbar.minExceedsTarget);
          return;
        }
        if (!isNaN(max) && !isNaN(target) && max < target) {
          toast.error(t.pricing.saveToolbar.maxBelowTarget);
          return;
        }
      }

      for (const nr of newProductRows) {
        if (!nr.itemId || !nr.pricingUnit) {
          errorRowIds.add(nr.id);
        } else if (!nr.tierEditValues.targetPrice) {
          errorRowIds.add(nr.id);
        }
      }
      for (const nr of newTierRows) {
        if (!nr.tierEditValues.targetPrice) {
          errorRowIds.add(nr.id);
        }
      }
      if (errorRowIds.size > 0) {
        setValidationErrors(errorRowIds);
        const hasIncomplete = newProductRows.some(
          (nr) => errorRowIds.has(nr.id) && (!nr.itemId || !nr.pricingUnit),
        );
        toast.error(
          hasIncomplete
            ? t.pricing.saveToolbar.incompleteNewRow
            : t.pricing.saveToolbar.targetPriceRequired,
        );
        return;
      }
      setValidationErrors(new Set());

      const resultingItems = pricingsList.map((pp) => {
        const tierChange = Array.from(pendingChanges.entries()).find(
          ([tid]) =>
            tierRows.find((r) => r.id === tid)?.itemPricingId === pp.id,
        );
        const pending = tierChange?.[1];
        return {
          id: pp.id,
          itemId: pending?.itemId ?? pp.item.id,
          pricingUnit: pending?.pricingUnit ?? pp.pricingUnit,
        };
      });
      for (const nr of newProductRows) {
        resultingItems.push({
          id: nr.id,
          itemId: nr.itemId,
          pricingUnit: nr.pricingUnit,
        });
      }
      const seen = new Set<string>();
      for (const item of resultingItems) {
        const key = `${item.itemId}:${item.pricingUnit}`;
        if (seen.has(key)) {
          toast.error(t.pricing.saveToolbar.duplicateItem);
          return;
        }
        seen.add(key);
      }
      for (const pp of pricingsList) {
        if (pendingDeletes.has(pp.id)) continue;
        const inlineTiers = newTierRows.filter(
          (nr) => nr.parentItemPricingId === pp.id,
        );
        const activeTierCount =
          pp.tiers.filter((tier) => !pendingTierDeletes.has(tier.id)).length +
          inlineTiers.length;
        if (activeTierCount <= 1) continue;
        let lastMaxAmount: string;
        if (inlineTiers.length > 0) {
          lastMaxAmount =
            inlineTiers[inlineTiers.length - 1].tierEditValues.maxAmount;
        } else {
          const activeTiers = pp.tiers.filter(
            (tier) => !pendingTierDeletes.has(tier.id),
          );
          const lastTier = activeTiers[activeTiers.length - 1];
          if (!lastTier) continue;
          const pending = pendingChanges.get(lastTier.id);
          lastMaxAmount = pending
            ? pending.maxAmount
            : String(lastTier.maxAmount ?? '');
        }
        if (lastMaxAmount.trim() !== '') {
          toast.error(t.pricing.saveToolbar.lastTierMustBeUnbounded);
          return;
        }
      }

      const tz = getLocalTimeZone();
      const localDT = new CalendarDateTime(
        effectiveDate.year,
        effectiveDate.month,
        effectiveDate.day,
        effectiveTime.hour,
        effectiveTime.minute,
        0,
      );
      const isoDate = toZoned(localDT, tz).toAbsoluteString();

      const changesByPP = new Map<string, Map<string, TierEditValues>>();
      for (const [tierId, entry] of pendingChanges) {
        const row = tierRows.find((r) => r.id === tierId);
        if (!row) continue;
        const ppId = row.itemPricingId;
        if (!changesByPP.has(ppId)) changesByPP.set(ppId, new Map());
        changesByPP.get(ppId)?.set(tierId, entry);
      }

      const newTiersByPP = new Map<string, NewInlineRowData[]>();
      for (const nr of newTierRows) {
        if (!nr.parentItemPricingId) continue;
        let list = newTiersByPP.get(nr.parentItemPricingId);
        if (!list) {
          list = [];
          newTiersByPP.set(nr.parentItemPricingId, list);
        }
        list.push(nr);
      }

      const tierDeletesByPP = new Map<string, Set<string>>();
      for (const tierId of pendingTierDeletes) {
        const row = tierRows.find((r) => r.id === tierId);
        if (!row) continue;
        let set = tierDeletesByPP.get(row.itemPricingId);
        if (!set) {
          set = new Set();
          tierDeletesByPP.set(row.itemPricingId, set);
        }
        set.add(tierId);
      }

      const affectedPPIds = new Set([
        ...changesByPP.keys(),
        ...newTiersByPP.keys(),
        ...tierDeletesByPP.keys(),
      ]);

      const batchItems: BatchSaveItem[] = [];
      for (const ppId of affectedPPIds) {
        const pp = pricingsList.find((p) => p.id === ppId);
        if (!pp) continue;
        const tierChanges = changesByPP.get(ppId);
        const inlineTiers = newTiersByPP.get(ppId);
        const deletedTierIds = tierDeletesByPP.get(ppId);

        const tiers: TierInput[] = pp.tiers
          .filter((tier) => !deletedTierIds?.has(tier.id))
          .map((tier, idx) => {
            const pending = tierChanges?.get(tier.id);
            if (pending) {
              return {
                ...buildTierInputFromEdit(pending, pricingMode),
                position: idx,
              };
            }
            return tierRecordToInput(tier, idx);
          });

        if (inlineTiers) {
          for (const nr of inlineTiers) {
            tiers.push({
              ...buildTierInputFromEdit(nr.tierEditValues, pricingMode),
              position: tiers.length,
            });
          }
        }

        const firstPending = tierChanges
          ? Array.from(tierChanges.values())[0]
          : undefined;
        const inputFields: BatchSaveItem['input'] = {
          tiersEffectiveAt: isoDate,
        };
        if (firstPending?.itemId) inputFields.itemId = firstPending.itemId;
        if (firstPending?.pricingUnit)
          inputFields.pricingUnit = firstPending.pricingUnit;

        batchItems.push({
          itemPricingId: ppId,
          input: inputFields,
          tiers,
        });
      }

      if (batchItems.length > 0) {
        await batchSave({ listId, items: batchItems });
      }

      for (const nr of newProductRows) {
        const tierInput = buildTierInputFromEdit(
          nr.tierEditValues,
          pricingMode,
        );
        await addItemPricing({
          itemId: nr.itemId,
          pricingUnit: nr.pricingUnit as Unit,
          position: pricingsList.length + newProductRows.indexOf(nr),
          tiers: [tierInput],
        });
      }

      if (hasReorder && reorderPending && reorderItemPricings) {
        await reorderItemPricings({ itemPricingIds: reorderPending });
      }

      if (hasDeletes) {
        for (const itemPricingId of pendingDeletes) {
          await deleteItemPricing({ itemPricingId });
        }
      }

      toast.success(t.pricing.saveToolbar.saved);
      setPendingChanges(new Map());
      setAdHocEvaluated(new Map());
      setNewInlineRows([]);
      setReorderPending(null);
      setPendingDeletes(new Set());
      setPendingTierDeletes(new Set());
      setEffectiveDate(today(getLocalTimeZone()));
      refetchItemPricings?.();
      refetchEvaluatedFormulas?.();
    } catch (err) {
      toast.error(
        t.pricing.itemPricings.toast.error(
          err instanceof Error ? err.message : 'Unknown error',
        ),
      );
    }
  }, [
    listId,
    pendingChanges,
    pendingDeletes,
    pendingTierDeletes,
    newInlineRows,
    reorderPending,
    tierRows,
    effectiveDate,
    effectiveTime,
    pricingsList,
    pricingMode,
    batchSave,
    addItemPricing,
    deleteItemPricing,
    reorderItemPricings,
    toast,
    t,
    refetchItemPricings,
    refetchEvaluatedFormulas,
  ]);

  const handleDiscard = React.useCallback(() => {
    setPendingChanges(new Map());
    setAdHocEvaluated(new Map());
    setNewInlineRows([]);
    setReorderPending(null);
    setPendingDeletes(new Set());
    setPendingTierDeletes(new Set());
    setEffectiveDate(today(getLocalTimeZone()));
    const now = new Date();
    setEffectiveTime(new Time(now.getHours(), now.getMinutes()));
    setSelectedCell(null);
  }, [setSelectedCell]);

  const revertRow = React.useCallback(
    (tierId: string) => {
      const row = flatTierRows.find((r) => r.id === tierId);
      if (row?.isPendingDelete) {
        setPendingDeletes((prev) => {
          const next = new Set(prev);
          next.delete(row.itemPricingId);
          return next;
        });
        return;
      }
      setPendingChanges((prev) => {
        const next = new Map(prev);
        next.delete(tierId);
        return next;
      });
      setAdHocEvaluated((prev) => {
        const next = new Map(prev);
        next.delete(tierId);
        return next;
      });
      setNewInlineRows((prev) => prev.filter((nr) => nr.id !== tierId));
    },
    [flatTierRows],
  );

  const missingItemErrors = React.useMemo(() => {
    const ids = new Set<string>();
    for (const nr of newInlineRows) {
      if (nr.parentItemPricingId) continue;
      if (nr.itemId) continue;
      const v = nr.tierEditValues;
      const hasValue =
        v.targetPrice ||
        v.minPrice ||
        v.maxPrice ||
        v.maxAmount ||
        v.scrapValue;
      if (hasValue) ids.add(nr.id);
    }
    return ids;
  }, [newInlineRows]);

  const pendingCount = React.useMemo(() => {
    let count = pendingChanges.size;
    count += newInlineRows.filter(
      (nr) => !nr.parentItemPricingId && nr.itemId,
    ).length;
    count += newInlineRows.filter((nr) => nr.parentItemPricingId).length;
    if (reorderPending) count += 1;
    count += pendingDeletes.size;
    count += pendingTierDeletes.size;
    return count;
  }, [
    pendingChanges.size,
    newInlineRows,
    reorderPending,
    pendingDeletes.size,
    pendingTierDeletes.size,
  ]);

  return {
    pendingChanges,
    adHocEvaluated,
    pendingCount,
    validationErrors:
      missingItemErrors.size > 0
        ? new Set([...validationErrors, ...missingItemErrors])
        : validationErrors,
    effectiveDate,
    setEffectiveDate,
    effectiveTime,
    setEffectiveTime,
    tierRows,
    flatTierRows,
    selectedCellValue,
    selectedCellEvaluatedValue,
    isAddDialogOpen,
    setIsAddDialogOpen,
    isVariablesPanelOpen,
    setIsVariablesPanelOpen,
    addTierTarget,
    setAddTierTarget,
    handleCellChange,
    handleFormulaBarChange,
    handleAddItemPricing,
    handleDeleteItemPricing,
    handleCopyItemPricing,
    handleAddTier,
    handleAddTierSubmit,
    handleBatchSave,
    handleDiscard,
    revertRow,
    variables,
    addEmptyRow,
    removeLastEmptyRow,
    addTierRow,
    deleteTier,
    reorderItems,
    reorderGroup,
    newInlineRows,
    isInlineRow,
    getInlineRowData,
    handleInlineRowChange,
    pendingFocusRowId,
    setPendingFocusRowId,
  };
};
